import {dataTestId} from '@hconnect/uikit'
import {ChatBubbleOutline, Edit, MoreVert, MyLocation} from '@mui/icons-material'
import {Box, Card, CardContent, Chip, IconButton, Stack, Typography, useTheme} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

import {StrengthTarget} from '../types'
import {canModifyTarget} from '../utils/dateUtils'

interface StrengthTargetListMobileCardsProps {
  targets: StrengthTarget[]
  unit: string | undefined
  isReadOnly: boolean
  timezone: string
  onEditTarget: (target: StrengthTarget) => void
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, target: StrengthTarget) => void
  onCommentClick: (target: StrengthTarget) => void
  formatDate: (dateString: string) => string
  formatValidFrom: (dateString: string) => string
}

interface CardActionsProps {
  target: StrengthTarget
  isReadOnly: boolean
  timezone: string
  onEditTarget: (target: StrengthTarget) => void
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, target: StrengthTarget) => void
  onCommentClick: (target: StrengthTarget) => void
}

const CardActions: React.FC<CardActionsProps> = ({
  target,
  isReadOnly,
  timezone,
  onEditTarget,
  onMenuOpen,
  onCommentClick
}) => {
  const theme = useTheme()

  if (isReadOnly) {
    return null
  }

  const canModify = canModifyTarget(target.id, target.validFrom, timezone)

  return (
    <Stack direction="row" spacing={0.5}>
      {target.comment && (
        <IconButton
          size="small"
          onClick={() => onCommentClick(target)}
          {...dataTestId(`comment_button_mobile_${target.id}`)}
        >
          <ChatBubbleOutline fontSize="small" />
        </IconButton>
      )}
      {canModify && (
        <>
          {target.isCurrent ? (
            <IconButton
              size="small"
              onClick={() => onEditTarget(target)}
              {...dataTestId(`edit_button_mobile_${target.id}`)}
            >
              <Edit fontSize="small" sx={{color: theme.palette.primary.main}} />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              onClick={(e) => onMenuOpen(e, target)}
              {...dataTestId(`menu_button_mobile_${target.id}`)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
          )}
        </>
      )}
    </Stack>
  )
}

interface CardHeaderProps {
  target: StrengthTarget
  unit: string | undefined
}

const CardHeader: React.FC<CardHeaderProps> = ({target, unit}) => {
  const {t} = useTranslation()
  const theme = useTheme()

  const formatValue = (value: number | undefined): string => {
    if (value === undefined) return '-'
    return `${value}${unit ? ` ${unit}` : ''}`
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {target.isCurrent && (
        <MyLocation
          fontSize="small"
          sx={{color: theme.palette.primary.main}}
          {...dataTestId(`current_icon_mobile_${target.id}`)}
        />
      )}
      <Typography variant="h6" sx={{fontWeight: 600}}>
        {formatValue(target.target)}
      </Typography>
      {target.isCurrent && (
        <Chip
          label={t('materialsSettings.strengthTargets.current')}
          size="small"
          color="primary"
          sx={{height: 24, fontSize: '12px'}}
          {...dataTestId(`current_chip_mobile_${target.id}`)}
        />
      )}
    </Stack>
  )
}

interface CardDataFieldProps {
  label: string
  value: string
}

const CardDataField: React.FC<CardDataFieldProps> = ({label, value}) => {
  const theme = useTheme()

  return (
    <Box>
      <Typography
        variant="caption"
        sx={{color: theme.palette.text.secondary, display: 'block', mb: 0.5}}
      >
        {label}
      </Typography>
      <Typography variant="body2" sx={{fontWeight: 500}}>
        {value}
      </Typography>
    </Box>
  )
}

export const StrengthTargetListMobileCards: React.FC<StrengthTargetListMobileCardsProps> = ({
  targets,
  unit,
  isReadOnly,
  timezone,
  onEditTarget,
  onMenuOpen,
  onCommentClick,
  formatDate,
  formatValidFrom
}) => {
  const {t} = useTranslation()
  const theme = useTheme()

  const formatValueWithUnit = (value: number | undefined): string => {
    if (value === undefined) return '-'
    return `${value}${unit ? ` ${unit}` : ''}`
  }

  return (
    <Stack spacing={2} {...dataTestId('strength_target_mobile_cards')}>
      {targets.map((target) => (
        <Card
          key={target.id}
          sx={{
            border: target.isCurrent ? `2px solid ${theme.palette.primary.main}` : 'none',
            boxShadow: target.isCurrent ? 3 : 1
          }}
          {...dataTestId(`strength_target_card_${target.id}`)}
        >
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <CardHeader target={target} unit={unit} />
              <CardActions
                target={target}
                isReadOnly={isReadOnly}
                timezone={timezone}
                onEditTarget={onEditTarget}
                onMenuOpen={onMenuOpen}
                onCommentClick={onCommentClick}
              />
            </Stack>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 2
              }}
            >
              <CardDataField
                label={t('materialsSettings.strengthTargets.lowerBound')}
                value={formatValueWithUnit(target.lowerBound)}
              />
              <CardDataField
                label={t('materialsSettings.strengthTargets.upperBound')}
                value={formatValueWithUnit(target.upperBound)}
              />
              <CardDataField
                label={t('materialsSettings.strengthTargets.validFrom')}
                value={formatValidFrom(target.validFrom)}
              />
              <CardDataField
                label={t('materialsSettings.strengthTargets.validTo')}
                value={target.validTo ? formatDate(target.validTo) : '-'}
              />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>
  )
}
